/**
 * Section minimale : catalogue des profils tarifaires d'organisation.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  createPricingProfile,
  deletePricingProfile,
  HttpError,
  listPricingProfiles,
} from '../services/organizationApi'
import type { PricingProfile } from '../types'
import { useTranslation } from 'react-i18next'

interface OrganizationPricingProfilesSectionProps {
  organizationId: number
  disabled?: boolean
}

export function OrganizationPricingProfilesSection({
  organizationId,
  disabled = false,
}: OrganizationPricingProfilesSectionProps) {
  const { t } = useTranslation('organizations')
  const [profiles, setProfiles] = useState<PricingProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [appliesTo, setAppliesTo] = useState<'camping' | 'refuge'>('camping')
  const [pricingMode, setPricingMode] = useState<'simple' | 'detailed' | 'free' | 'desuet'>('simple')
  const [simpleMin, setSimpleMin] = useState('')
  const [simpleMax, setSimpleMax] = useState('')
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listPricingProfiles(organizationId, {
        include_ancestors: false,
      })
      setProfiles(list)
    } catch (err) {
      setError(err instanceof HttpError ? err.message : t('OrganizationPricingProfilesSection.chargement_impossible'))
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void reload()
  }, [reload])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError(t('OrganizationPricingProfilesSection.nom_profil_requis'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      await createPricingProfile(organizationId, {
        name: name.trim(),
        applies_to: appliesTo,
        pricing_mode: pricingMode,
        simple_price_min: simpleMin.trim() ? parseFloat(simpleMin) : null,
        simple_price_max: simpleMax.trim() ? parseFloat(simpleMax) : null,
        taxes_included: true,
        access_included: true,
      })
      setName('')
      setSimpleMin('')
      setSimpleMax('')
      await reload()
    } catch (err) {
      setError(err instanceof HttpError ? err.message : t('OrganizationPricingProfilesSection.creation_impossible'))
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (profileId: number) => {
    if (!window.confirm(t('OrganizationPricingProfilesSection.supprimer_profil_tarifaire'))) return
    setBusy(true)
    setError(null)
    try {
      await deletePricingProfile(organizationId, profileId)
      await reload()
    } catch (err) {
      setError(err instanceof HttpError ? err.message : t('OrganizationPricingProfilesSection.suppression_impossible'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="organization-settings__field organization-settings__pricing-profiles">
      <h3 className="organization-settings__subsection-title">{t('OrganizationPricingProfilesSection.profils_tarifaires')}</h3>
      <p className="organization-settings__hint">
        {t('OrganizationPricingProfilesSection.catalogues_nommes_camping_ou_refuge')}
      </p>

      {error && (
        <div className="organization-settings__error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p className="organization-settings__hint">{t('OrganizationPricingProfilesSection.chargement')}</p>
      ) : profiles.length === 0 ? (
        <p className="organization-settings__hint">{t('OrganizationPricingProfilesSection.aucun_profil_organisation')}</p>
      ) : (
        <ul className="organization-settings__pricing-list">
          {profiles.map((p) => (
            <li key={p.id} className="organization-settings__pricing-item">
              <span>
                <strong>{p.name}</strong>
                {' — '}
                {p.applies_to === 'camping' ? t('OrganizationPricingProfilesSection.camping') : t('OrganizationPricingProfilesSection.refuge')}
                {p.pricing_mode ? ` · ${p.pricing_mode}` : ''}
                {p.simple_price_min != null && p.simple_price_min !== ''
                  ? t('OrganizationPricingProfilesSection.des', { simple_price_min: p.simple_price_min })
                  : ''}
              </span>
              <button
                type="button"
                className="organization-settings__button organization-settings__button--secondary"
                onClick={() => void handleDelete(p.id)}
                disabled={disabled || busy}
              >
                {t('OrganizationPricingProfilesSection.supprimer')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form className="organization-settings__pricing-create" onSubmit={(e) => void handleCreate(e)}>
        <p className="organization-settings__label">{t('OrganizationPricingProfilesSection.nouveau_profil')}</p>
        <div className="organization-settings__pricing-create-row">
          <input
            type="text"
            className="organization-settings__input"
            placeholder={t('OrganizationPricingProfilesSection.nom')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={disabled || busy}
            required
          />
          <select
            className="organization-settings__select"
            value={appliesTo}
            onChange={(e) => setAppliesTo(e.target.value as 'camping' | 'refuge')}
            disabled={disabled || busy}
          >
            <option value="camping">{t('OrganizationPricingProfilesSection.camping')}</option>
            <option value="refuge">{t('OrganizationPricingProfilesSection.refuge')}</option>
          </select>
          <select
            className="organization-settings__select"
            value={pricingMode}
            onChange={(e) =>
              setPricingMode(e.target.value as 'simple' | 'detailed' | 'free' | 'desuet')
            }
            disabled={disabled || busy}
          >
            <option value="simple">{t('OrganizationPricingProfilesSection.simplifie')}</option>
            <option value="detailed">{t('OrganizationPricingProfilesSection.detaille')}</option>
            <option value="free">{t('OrganizationPricingProfilesSection.gratuit')}</option>
            <option value="desuet">{t('OrganizationPricingProfilesSection.desuet')}</option>
          </select>
          <input
            type="number"
            step="0.01"
            min={0}
            className="organization-settings__input"
            placeholder={t('OrganizationPricingProfilesSection.min')}
            value={simpleMin}
            onChange={(e) => setSimpleMin(e.target.value)}
            disabled={disabled || busy}
          />
          <input
            type="number"
            step="0.01"
            min={0}
            className="organization-settings__input"
            placeholder={t('OrganizationPricingProfilesSection.max')}
            value={simpleMax}
            onChange={(e) => setSimpleMax(e.target.value)}
            disabled={disabled || busy}
          />
          <button
            type="submit"
            className="organization-settings__button"
            disabled={disabled || busy}
          >
            {t('OrganizationPricingProfilesSection.creer')}
          </button>
        </div>
      </form>
    </section>
  )
}
